import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
  faGear,
  faMagnifyingGlassPlus,
  faThumbsUp,
  faThumbsDown,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { capitalizeFirstLetter } from "@utils/helperFunctions";
import {
  VersionSection,
  ApprovalStepSection,
  ApprovalRow,
} from "../types/ApprovalRow";
import useStyles from "../styles/useStyles";

interface LobOption {
  id: string;
  name: string;
  checked: boolean;
}

interface UserOption {
  id: string;
  name: string;
}

interface StepSummaryRow {
  id: string;
  stepDescription: string;
  stepStatus: string;
  stepId: string;
  step: string;
  approvalCount: number;
  collapsedData?: React.ReactNode;
  isStepRow: true;
}

type ApprovalAction = (approvalId: string, comment: string) => Promise<boolean>;

interface Props {
  versions: VersionSection[];
  projectId: string;
  users: UserOption[];
  lobs: LobOption[];
  onApprove: ApprovalAction;
  onDecline: ApprovalAction;
  onCancel: ApprovalAction;
  onRescope: ApprovalAction;
  onViewVersionChanges: (versionId: string) => void;
  onCreateApproval: (
    stepId: string,
    userId: string,
    status: string,
    lobIds: string[]
  ) => Promise<boolean>;
  existingApprovalUserIds: (stepId: string) => Set<string>;
}

const Show: React.FC<Props> = ({
  versions,
  projectId,
  users,
  lobs: lobsProp,
  onApprove,
  onDecline,
  onCancel,
  onRescope,
  onViewVersionChanges,
  onCreateApproval,
  existingApprovalUserIds,
}) => {
  const styles = useStyles();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [openMenuStepId, setOpenMenuStepId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Add Approver modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalStepId, setAddModalStepId] = useState("");
  const [addModalStep, setAddModalStep] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [requiredStatus, setRequiredStatus] = useState("");
  const [lobSelections, setLobSelections] = useState<LobOption[]>([]);

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewApprovalId, setReviewApprovalId] = useState("");
  const [reviewStep, setReviewStep] = useState("");
  const [reviewComment, setReviewComment] = useState("");

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        !target.closest(".fa-gear")
      ) {
        setOpenMenuStepId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const isSectionExpanded = (id: string) => expandedSections[id] ?? true;

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const openAddApproverModal = (stepId: string, step: string) => {
    setAddModalStepId(stepId);
    setAddModalStep(step);
    setSelectedUserId("");
    setRequiredStatus("");
    setLobSelections(lobsProp.map((l) => ({ ...l, checked: false })));
    setOpenMenuStepId(null);
    setShowAddModal(true);
  };

  const handleAddApproverSubmit = async () => {
    const lobIds = lobSelections
      .filter((l) => l.checked)
      .map((l) => l.id);
    const success = await onCreateApproval(
      addModalStepId,
      selectedUserId,
      requiredStatus.toLowerCase(),
      lobIds
    );
    if (success) {
      setShowAddModal(false);
    }
  };

  const availableUsers = (stepId: string): UserOption[] => {
    const existing = existingApprovalUserIds(stepId);
    return users.filter((u) => !existing.has(u.id));
  };

  const openReviewModal = (approvalId: string, step: string) => {
    setReviewApprovalId(approvalId);
    setReviewStep(step);
    setReviewComment("");
    setShowReviewModal(true);
  };

  const handleReviewAction = async (action: ApprovalAction) => {
    const success = await action(reviewApprovalId, reviewComment);
    if (success) {
      setShowReviewModal(false);
    }
  };

  const buildDetailColumns = (
    step: ApprovalStepSection
  ): ColumnDef<ApprovalRow>[] => [
    {
      id: "status",
      header: "Status",
      cell: (props) => (
        <span>{capitalizeFirstLetter(props.row.original.status)}</span>
      ),
    },
    {
      id: "approverName",
      header: "User",
      cell: (props) => <span>{props.row.original.approverName}</span>,
    },
    {
      id: "reason",
      header: "Reason",
      cell: (props) => <span>{props.row.original.reason}</span>,
    },
    {
      id: "comment",
      header: "Comment",
      cell: (props) => {
        const row = props.row.original;
        return (
          <span>
            {row.comment}
            {row.canApprove && (
              <span
                style={{ float: "right", cursor: "pointer", color: "#418172" }}
                onClick={() => openReviewModal(row.approvalId, step.step)}
              >
                Review &rarr;
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "completedAt",
      header: "Date",
      cell: (props) => {
        const row = props.row.original;
        if (row.status === "required" || row.status === "optional") return null;
        return <span>{formatDate(row.completedAt)}</span>;
      },
    },
  ];

  const summaryColumns: ColumnDef<StepSummaryRow>[] = [
    {
      id: "stepStatus",
      header: "Status",
      cell: (props) => {
        const status = props.row.original.stepStatus;
        const style: React.CSSProperties = {};
        if (status === "declined") {
          style.fontWeight = 700;
          style.color = "#b90200";
        } else if (status === "approved") {
          style.fontWeight = 700;
          style.color = "#418172";
        }
        return <span style={style}>{capitalizeFirstLetter(status)}</span>;
      },
    },
    {
      id: "stepDescription",
      header: "Approval Step",
      cell: (props) => {
        const row = props.row.original;
        return (
          <span style={{ fontWeight: 600 }}>{row.stepDescription}</span>
        );
      },
    },
    {
      id: "approvalCount",
      header: "Approvers",
      cell: (props) => {
        const row = props.row.original;
        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", width: "100%" }}>
            <span>{row.approvalCount}</span>
            {row.stepStatus === "pending" && (
              <>
                <FontAwesomeIcon
                  icon={faGear}
                  className={styles.gearIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuStepId((prev) =>
                      prev === row.stepId ? null : row.stepId
                    );
                  }}
                />
                {openMenuStepId === row.stepId && (
                  <div ref={menuRef} className={styles.cogMenu}>
                    <div
                      className={styles.cogMenuItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        openAddApproverModal(row.stepId, row.step);
                      }}
                    >
                      Add Approver
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  const buildStepRows = (steps: ApprovalStepSection[]): StepSummaryRow[] =>
    steps.map((step) => ({
      id: `step-${step.id}`,
      stepDescription: step.description,
      stepStatus: step.status,
      stepId: step.id,
      step: step.step,
      approvalCount: step.approvals.length,
      isStepRow: true as const,
      collapsedData:
        step.approvals.length > 0 ? (
          <DataTable
            data={step.approvals}
            columns={buildDetailColumns(step)}
            selectable={false}
            classForTable={`${styles.detailTable} ${styles.detailColumnWidths}`}
            striped
          />
        ) : undefined,
    }));

  const renderVersion = (version: VersionSection) => {
    const expanded = isSectionExpanded(version.id);
    const stepRows = buildStepRows(version.steps);
    const hasCollapsible = stepRows.some((r) => r.collapsedData);
    const pendingExpandedIds = stepRows
      .filter((r) => r.stepStatus === "pending" && r.collapsedData)
      .map((r) => r.id);

    return (
      <div key={version.id} className={styles.section}>
        <div
          className={styles.sectionHeader}
          onClick={() => toggleSection(version.id)}
        >
          <span className={styles.sectionTitle}>
            {version.name}
            <span
              className={styles.versionLink}
              style={{ marginLeft: 16 }}
              onClick={(e) => {
                e.stopPropagation();
                onViewVersionChanges(version.id);
              }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlassPlus} style={{ marginRight: 4 }} />
              See Version Changes
            </span>
          </span>
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className={styles.sectionChevron}
          />
        </div>
        {expanded && (
          <div className={styles.sectionBody}>
            {version.steps.length > 0 ? (
              <DataTable
                data={stepRows}
                columns={summaryColumns}
                selectable={false}
                isCollapsible={hasCollapsible}
                defaultExpandedRowIds={pendingExpandedIds}
                classForTable={`${styles.sectionTable} ${styles.summaryColumnWidths}`}
                striped
              />
            ) : (
              <div className={styles.noData}>No approval steps</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const addModalBody = (
    <div>
      <Row>
        <Col>
          <Form.Group>
            <Form.Label>Additional Approver *</Form.Label>
            <select
              className="form-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value=""></option>
              {availableUsers(addModalStepId).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </Form.Group>
        </Col>
        {addModalStep === "technical_approval" && (
          <Col>
            <Form.Label>Lines of Business to Approve</Form.Label>
            {lobSelections.map((lob) => (
              <Form.Check
                key={lob.id}
                id={`lob-${lob.id}`}
                label={lob.name}
                checked={lob.checked}
                onChange={() =>
                  setLobSelections((prev) =>
                    prev.map((l) =>
                      l.id === lob.id
                        ? { ...l, checked: !l.checked }
                        : l
                    )
                  )
                }
              />
            ))}
          </Col>
        )}
        <Col>
          <Form.Group>
            <Form.Label>Required/Optional *</Form.Label>
            <select
              className="form-control"
              value={requiredStatus}
              onChange={(e) => setRequiredStatus(e.target.value)}
            >
              <option value=""></option>
              <option value="required">Required</option>
              <option value="optional">Optional</option>
            </select>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const reviewModalBody = (
    <div>
      <Form.Group>
        <Form.Label>Comments</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={reviewComment}
          onChange={(e) => setReviewComment(e.target.value)}
        />
      </Form.Group>
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <Button
          variant="primary"
          onClick={() => handleReviewAction(onApprove)}
        >
          <FontAwesomeIcon icon={faThumbsUp} style={{ marginRight: 4 }} />
          Approve
        </Button>
        {reviewStep !== "business_approval" && (
          <Button
            variant="secondary"
            onClick={() => handleReviewAction(onDecline)}
          >
            <FontAwesomeIcon icon={faThumbsDown} style={{ marginRight: 4 }} />
            Decline
          </Button>
        )}
        {reviewStep === "business_approval" && (
          <>
            <Button
              variant="secondary"
              onClick={() => handleReviewAction(onRescope)}
            >
              <FontAwesomeIcon icon={faThumbsDown} style={{ marginRight: 4 }} />
              Adjust Scope
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleReviewAction(onDecline)}
            >
              <FontAwesomeIcon icon={faThumbsDown} style={{ marginRight: 4 }} />
              Adjust Price
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm("Are you sure? This will cancel the project.")) {
                  handleReviewAction(onCancel);
                }
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ marginRight: 4 }} />
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={`${styles.page} ${styles.columnWidths}`}>
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Approvals</Col>
          </Row>
        </Card.Header>
        <hr className={styles.hr} />
        <Card.Body>
          {versions.length > 0 ? (
            versions.map(renderVersion)
          ) : (
            <div className={styles.noData}>No versions with approvals</div>
          )}
        </Card.Body>
      </Card>
      <ScopeStackModal
        modalTitle="Request Additional Approval"
        modalBody={addModalBody}
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        button1Text="Cancel"
        handleButton1Click={() => setShowAddModal(false)}
        button2Text="Save"
        handleButton2Click={handleAddApproverSubmit}
        button2Disabled={!selectedUserId || !requiredStatus}
      />
      <ScopeStackModal
        modalTitle="Approve/Decline"
        modalBody={reviewModalBody}
        show={showReviewModal}
        handleClose={() => setShowReviewModal(false)}
      />
    </div>
  );
};

export default Show;
